import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Grid } from "@mui/material";
import { IEventLedgerList } from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
import { ITableColumn } from "src/components/FilterSearchTable/TableComponents/TableHeader";
import { TextField } from "src/components/FormikComponents/TextField";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
// Styling
import { Theme, useTheme } from "@mui/material";

interface ICrewPairingLedgerProps {
  id: string;
  path: string[];
}

const crewPairingLedgerStyles = (theme: Theme): object => ({
  flexGrow: 1,
  margin: "1rem",
  "& .shortcode_filter": {
    "& .input_control": {
      "& label": { width: "auto" },
      "& .input-base": { width: 50 },
    },
  },
  "& .employeeid_filter": {
    "& .input_control": {
      "& label": { width: "auto" },
      "& .input-base": { width: 100 },
    },
  },
  "& .crewname_filter": {
    "& .input_control": {
      "& label": { width: "auto" },
      "& .input-base": { width: 150 },
    },
  },
  "& .status_type_filter": {
    "& .input_control label": { width: "auto" },
  },
  "& .refresh_btn": {
    margin: "5px",
    padding: "2px 8px",
    height: 20,
    fontSize: 10,
    "& .refresh_icon": {
      width: "0.8em",
      height: "0.8em",
    },
  },
  "& .loadingSpinner": {
    marginTop: "-18px",
    marginLeft: "-16px",
    height: "calc(100% - 34px)",
  },
});

export const tableInfo: ITableColumn[] = [
  {
    id: "changeId",
    value: "changeId",
    index: 0,
    numeric: false,
    label: "Change Id",
    type: "string",
    width: 10,
    visible: false,
    filter: false,
  },
  {
    id: "event[start][time]",
    value: "event[start][time]",
    index: 1,
    numeric: false,
    label: "Event Impacted Time",
    type: "epochWithTimezone",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "crewMember[employeeId]",
    value: "crewMember[employeeId]",
    index: 2,
    numeric: false,
    label: "Employee #",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "crewMember",
    value: "crewMember",
    index: 3,
    numeric: false,
    label: "Crew",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "details",
    value: "details",
    index: 4,
    numeric: false,
    label: "Details",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "prevValue[time]",
    value: "prevValue[time]",
    index: 5,
    numeric: false,
    label: "Original Value",
    type: "epochWithTimezone",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "currentValue[time]",
    value: "currentValue[time]",
    index: 6,
    numeric: false,
    label: "Current Value",
    type: "epochWithTimezone",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "event",
    value: "event",
    index: 7,
    numeric: false,
    label: "Event",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "acknowledged",
    value: "acknowledged",
    index: 8,
    numeric: true,
    label: "Status",
    type: "object",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "daysSinceChange",
    value: "daysSinceChange",
    index: 9,
    numeric: true,
    label: "Days Since Change",
    type: "number",
    width: 10,
    visible: true,
    filter: false,
  },
];

export const tableDesc = {
  tableTitle: "Crew Pairing Change Ledger",
  tableDescription: "",
  noMessage: "No unacknowledged pairing changes found.",
};

/**
 * Function returned pairing check-in status with applied filters.
 * @param rows - table rows
 * @param flightNumber - flight number filter
 * @param origin - origin filter
 * @param destination - destination filter
 */
const filterTableRows = (
  rows: IEventLedgerList[],
  crewName: string,
  employeeId: string,
  shortCode: string
): IEventLedgerList[] => {
  const filteredRows = rows.filter((row: IEventLedgerList) => {
    const fullCrewName = `${row.crewMember?.firstName} ${row.crewMember?.lastName}`;
    const filter1 = crewName ? fullCrewName?.toLowerCase().includes(crewName.toLowerCase()) : true;
    const filter2 = employeeId ? row.crewMember?.employeeId.startsWith(employeeId) : true;
    const filter3 = shortCode ? row.event?.shortId?.toLowerCase().startsWith(shortCode.toLowerCase()) : true;
    return filter1 && filter2 && filter3;
  });
  return filteredRows;
};

/**
 * CrewPairingLedgerWindow - Retrieves pairing changes that crew have not yet acknowledged so they can be actioned.
 * @param props - Contains the window id and the window path for this mosaic. Helpful to reference parent container
 */
export const CrewPairingLedgerWindow = (props: ICrewPairingLedgerProps): JSX.Element => {
  const { id } = props;
  const theme: Theme = useTheme();

  const [ledgerData, setLedgerData] = useState<IEventLedgerList[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const refreshStatus = useCallback(async () => {
    setIsLoading(true);
    const response: any = await AmplifyAPIWrapper.Get<IEventLedgerList[]>("/eventledgers/crews/");
    if (response && Array.isArray(response)) {
      setLedgerData(response);
    }
    setIsLoading(false);
  }, []);

  // Attempt to load check-in status upon loading component.
  useEffect(() => {
    if (refreshStatus) refreshStatus();
  }, [refreshStatus]);

  const [crewName, setCrewName] = useState<string>(null);
  const [employeeId, setEmployeeId] = useState<string>(null);
  const [shortCode, setShortCode] = useState<string>(null);

  const filteredTableRows = useMemo(() => {
    return filterTableRows(ledgerData, crewName, employeeId, shortCode);
  }, [ledgerData, crewName, employeeId, shortCode]);

  const tableData = useMemo(() => {
    return [
      {
        id: "CrewPairingChangeLedger",
        dataType: "CrewPairingChangeLedger",
        label: "Crew Pairing Change Ledger",
        data: filteredTableRows,
        tableInfo: tableInfo,
        tableInfoDisplay: tableDesc,
      },
    ];
  }, [filteredTableRows]);

  return (
    <Grid sx={crewPairingLedgerStyles(theme)} container direction="column">
      <SpinnerProgress
        className={"loadingSpinner"}
        loading={isLoading}
        message="Loading unacknowledged pairing changes"
        fullscreen={false}
      />
      <Grid item>
        <FilterSearchTable
          id={id}
          events={tableData}
          uniqueKey={"event[start][time]"}
          orderDirection={"asc"}
          hideSelectionCheckboxes={true}
          hideFocusSelected={true}
          defaultRowsPerPage={10}
          selectModeSingle={true}
          hideFilter={false}
          disableRowSelect={true}
          showUTCLocalToggle={true}
          optionalControls={
            <Grid container style={{ width: "auto" }}>
              <Grid item>
                <TextField
                  label="Filter Pairing"
                  className="shortcode_filter"
                  name="checkin_status_shortcode_filter"
                  value={shortCode}
                  onChange={(event: any) => {
                    const value = event.target.value ?? null;
                    setShortCode(value);
                  }}
                  type="text"
                  fullWidth={false}
                />
              </Grid>

              <Grid item>
                <TextField
                  label="Filter Employee #"
                  className="employeeid_filter"
                  name="checkin_status_employeeid_filter"
                  value={employeeId}
                  onChange={(event: any) => {
                    const value = event.target.value ?? null;
                    setEmployeeId(value);
                  }}
                  type="text"
                  fullWidth={false}
                />
              </Grid>

              <Grid item>
                <TextField
                  label="Filter Crew"
                  className="crewname_filter"
                  name="checkin_status_crewname_filter"
                  value={crewName}
                  onChange={(event: any) => {
                    const value = event.target.value ?? null;
                    setCrewName(value);
                  }}
                  type="text"
                  fullWidth={false}
                />
              </Grid>

              <Button
                className="refresh_btn"
                id={"refresh_checkin_status_btn"}
                onClick={() => refreshStatus()}
                color="primary"
                disabled={isLoading}
                variant="contained"
              >
                {"Refresh"}
              </Button>
            </Grid>
          }
        />
      </Grid>
    </Grid>
  );
};
